<template lang="pug">
.captions-gradient(:class="[isCaptionOverflowing && !isCaptionScrolledToBottom && show ? 'h-full' : 'h-0']")
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { nextTick } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  caption: {
    type: String as PropType<string | null>,
    default: null,
  },
})

const { show, caption } = toRefs(props)

const isCaptionScrolledToBottom = ref(false)
const isCaptionOverflowing = ref(true)
const captionsTextContainerRef: Ref<HTMLElement | null> = ref(null)

onMounted(() => {
  captionsTextContainerRef.value = document.querySelector('.captions-text-container')

  if (!captionsTextContainerRef.value) return
  captionsTextContainerRef.value.addEventListener('scroll', setIsCaptionScrolledToBottom)
  setIsCaptionScrolledToBottom()
  setIsCaptionOverflowing()
})

onUnmounted(() => {
  if (!captionsTextContainerRef.value) return
  document.removeEventListener('scroll', setIsCaptionScrolledToBottom)
})

const setIsCaptionScrolledToBottom = async () => {
  if (!captionsTextContainerRef.value) return
  await nextTick()
  isCaptionScrolledToBottom.value =
    captionsTextContainerRef.value.scrollHeight - captionsTextContainerRef.value.scrollTop - captionsTextContainerRef.value.clientHeight < 1
}

const setIsCaptionOverflowing = async () => {
  if (!captionsTextContainerRef.value) return
  await nextTick()
  isCaptionOverflowing.value = captionsTextContainerRef.value.scrollHeight > captionsTextContainerRef.value.clientHeight
}

watch(
  [show, caption],
  async ([show]) => {
    if (!show) return
    // caption needs to render before we check the container for overflow
    await nextTick()
    setIsCaptionOverflowing()
    setIsCaptionScrolledToBottom()
  },
  { immediate: true }
)
</script>

<style lang="stylus" scoped>
// caption gradient
.captions-gradient
  @apply transition-all duration-500 ease-in-out
  z-index: 10
  position: absolute
  bottom: 0
  width: 100%
  left: 0
  content: ""
  background: linear-gradient(to top,
    rgba(34,34,34, 0.9),
    rgba(0,0,0, 0) 30%
  )
  pointer-events: none; /* so the text is still selectable */
</style>
